import { DataNode } from 'components/WorkspaceEditor/types/types'
import { ChangeType, MapChange, PendingOperation } from '../types'

/**
 * Determines if a node represents anonymous temporary content
 * Temporary content is used to track pending operations that are not yet added to the main content map
 * It is identified by a single content item node with empty text and an anonymous user ID
 * @param {DataNode | undefined} node - The node to check
 * @returns {boolean} - True if the node is anonymous temporary content, false otherwise
 */
export const isAnonymousTemporaryContent = (
    node: DataNode | undefined
): boolean => {
    return Boolean(
        node &&
            node.contentItemNodes?.length === 1 &&
            node.contentItemNodes[0]?.text === '' &&
            node.contentItemNodes[0]?.authoredUserId === 'anonymous1'
    )
}

/**
 * Checks if the basic trigger conditions are met for adding content
 * This function checks if the content node addition should be triggered based on the flags
 * - shouldTriggerNewContent: Indicates if new content node should be added
 * - fromHandleKeyDown: Indicates if the action is triggered from a key down event
 * @param {boolean} shouldTriggerNewContent - Flag to determine if new content should be added
 * @param {boolean} fromHandleKeyDown - Flag to indicate if the action is triggered from a key down event
 * @returns {boolean} - True if both conditions are met, false otherwise
 */
export const shouldTriggerContentAddition = (
    shouldTriggerNewContent: boolean,
    fromHandleKeyDown: boolean
): boolean => {
    return shouldTriggerNewContent && fromHandleKeyDown
}

/**
 * Creates a unique operation ID for tracking pending operations
 * This ID is generated using the current timestamp and a random number
 * @returns {string} - A unique operation ID
 * This ID can be used to track pending operations in the global operations map
 * It is formatted as "timestamp_randomNumber" to ensure uniqueness
 * This is useful for identifying operations that are not yet added to the main content map
 */
export const createOperationId = (): string => {
    return `${Date.now()}_${Math.random()}`
}

/**
 * Creates a pending operation for a given node
 * This function generates a unique ID and captures the node's key and type
 * @param {DataNode} node - The node for which the pending operation is created
 * @return {PendingOperation} - The created pending operation object
 * This operation is used to track temporary content that is not yet added to the main content map
 * It includes the node's key, type, and a timestamp for tracking purposes
 */
export const createPendingOperation = (node: DataNode): PendingOperation => {
    return {
        id: createOperationId(),
        originalNodeKey: node.key,
        isAnonymousTemporaryContent: isAnonymousTemporaryContent(node),
        timestamp: Date.now(),
    }
}

/**
 * Cleans up expired operations from a global operations map
 * This function iterates through the operations map and removes entries that have exceeded a specified timeout
 * @param {Map<string, PendingOperation>} operationsMap - The map of pending operations to clean up
 * @param {number} timeoutMs - The timeout in milliseconds after which operations are considered expired
 * This is useful for preventing memory leaks and ensuring that only active operations are retained
 * It checks the timestamp of each operation and removes those that are older than the specified timeout
 */
export const cleanupExpiredOperations = (
    operationsMap: Map<string, PendingOperation>,
    timeoutMs: number
): void => {
    const now = Date.now()
    for (const [id, operation] of operationsMap.entries()) {
        if (now - operation.timestamp > timeoutMs) {
            operationsMap.delete(id)
        }
    }
}

/**
 * Compares two content item maps and returns detected changes
 * This function identifies new or updated content items by comparing the current and previous maps
 * @param {Map<string, DataNode>} currentMap - The current content item map
 * @param {Map<string, DataNode>} previousMap - The previous content item map
 * @return {MapChange[]} - An array of detected changes
 * Each change includes the content item ID, type of change (new or updated), and the corresponding node
 * This is useful for tracking changes in the content structure, such as additions or updates to nodes
 */
export const detectMapChanges = (
    currentMap: Map<string, DataNode>,
    previousMap: Map<string, DataNode>
): MapChange[] => {
    const changes: MapChange[] = []

    for (const [contentItemId, currentNode] of currentMap) {
        const previousNode = previousMap.get(contentItemId)

        if (!previousNode) {
            // Completely new contentItemId
            changes.push({
                contentItemId,
                changeType: 'new',
                node: currentNode,
            })
        } else if (previousNode !== currentNode) {
            // Same contentItemId but different node reference (updated)
            changes.push({
                contentItemId,
                changeType: 'updated',
                node: currentNode,
                previousNode,
            })
        }
    }

    return changes
}

/**
 * Checks if a change represents a newly added content item
 * @param change - The map change to check
 * @returns True if the change is a new node with ADDED changeType
 */
const isNewlyAddedContent = (change: MapChange): boolean => {
    return (
        change.changeType?.toUpperCase() === ChangeType.NEW &&
        change.node?.contentItemNodes?.some(x => x.changeType?.toUpperCase() === ChangeType.ADDED) === true
    )
}

/**
 * Checks if a change represents new content with any non-null changeType
 * @param change - The map change to check
 * @returns True if the change is a new node with any tracked changes
 */
const isNewContentWithChanges = (change: MapChange): boolean => {
    return (
        change.changeType?.toUpperCase() === ChangeType.NEW &&
        change.node?.contentItemNodes?.some(x => x.changeType !== null) === true
    )
}

/**
 * Checks if a change represents an updated content item
 * @param change - The map change to check
 * @returns True if the change is an updated node
 */
const isUpdatedContent = (change: MapChange): boolean => {
    return change.changeType?.toUpperCase() === ChangeType.UPDATED
}

/**
 * Gets the first node from detected changes, prioritizing new nodes over updated ones
 * This function returns the first changed node based on the type of change detected
 * @param {MapChange[]} changes - The array of detected changes
 * @return {DataNode | null} - The first changed node or null if no changes are found
 * This is useful for quickly identifying the most relevant change in the content item structure
 * It prioritizes new nodes over updated ones to ensure that the most significant change is returned
 */
export const getFirstChangedNode = (changes: MapChange[]): DataNode | null => {
    return (
        changes.find(isNewlyAddedContent)?.node ??
        changes.find(isNewContentWithChanges)?.node ??
        changes.find(isUpdatedContent)?.node ??
        null
    )
}
